import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, throwError, finalize, firstValueFrom, of } from 'rxjs';
import { Auth, LoginApiResponse, isLoginTokenData } from '../../features/auth/service/auth';
import {
  ProfileResponse,
  ProfileService,
  ProfileUserData,
} from '../../features/profile/service/profile';
import { environment } from '../../../environments/environment';

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface UserInfo {
  id: number;
  username: string;
  email: string;
  full_name: string;
  is_active: boolean;
  role: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T | null;
}

const TOKEN_KEY = 'pyngerp_token';
const REFRESH_KEY = 'pyngerp_refresh';
const USER_KEY = 'pyngerp_user';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private idpAuth = inject(Auth);
  private profileService = inject(ProfileService);

  readonly API_BASE = environment.apiBaseUrl;

  readonly token = signal<string | null>(this.loadToken());
  readonly currentUser = signal<UserInfo | null>(this.loadUser());
  /** Profile from `GET /v1/user/profile` — preferred for display name in shell/navbar after My Profile loads. */
  readonly apiProfile = signal<ProfileUserData | null>(null);
  readonly isAuthenticated = computed(() => !!this.token());
  /** True while the IDP login request is in flight. */
  readonly loginLoading = signal(false);
  /** True while the IDP logout request is in flight. */
  readonly logoutLoading = signal(false);

  /** Single in-flight refresh so parallel 401s share one token exchange. */
  private refreshChain: Promise<void> | null = null;

  private loadToken(): string | null {
    try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
  }

  private loadUser(): UserInfo | null {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }

  getToken(): string | null {
    return this.token();
  }

  getRefreshToken(): string | null {
    try {
      return localStorage.getItem(REFRESH_KEY);
    } catch {
      return null;
    }
  }

  /**
   * Refreshes the access token using the stored refresh token.
   * On failure, clears the session and navigates to login.
   */
  refreshAccessToken(): Promise<void> {
    if (this.refreshChain) return this.refreshChain;
    const rt = this.getRefreshToken();
    if (!rt) {
      this.clearLocalSession();
      void this.router.navigate(['/auth/login']);
      return Promise.reject(new Error('No refresh token'));
    }
    this.refreshChain = firstValueFrom(
      this.idpAuth.refresh(rt).pipe(
        tap((res) => {
          if (!res.status || !isLoginTokenData(res.data)) {
            throw new Error('Refresh rejected');
          }
          this.storeTokens(res.data.accessToken, res.data.refreshToken ?? rt);
        }),
      ),
    )
      .then(() => undefined)
      .catch(() => {
        this.clearLocalSession();
        void this.router.navigate(['/auth/login']);
        throw new Error('Session expired. Please sign in again.');
      })
      .finally(() => {
        this.refreshChain = null;
      });
    return this.refreshChain;
  }

  login(usernameOrEmail: string, password: string): Observable<LoginApiResponse> {
    this.loginLoading.set(true);
    return this.idpAuth.login(usernameOrEmail.trim(), password).pipe(
      tap((res) => {
        if (res.status && isLoginTokenData(res.data)) {
          this.storeTokens(res.data.accessToken, res.data.refreshToken);
        }
      }),
      finalize(() => this.loginLoading.set(false)),
      catchError((err) => throwError(() => err)),
    );
  }

  register(email: string, username: string, fullName: string, password: string): Observable<ApiResponse<UserInfo>> {
    return this.http
      .post<ApiResponse<UserInfo>>(`${this.API_BASE}/auth/register`, {
        email,
        username,
        full_name: fullName,
        password,
      })
      .pipe(catchError((err) => throwError(() => err)));
  }

  /**
   * Calls the server logout endpoint (Bearer + tenant headers via interceptors),
   * then clears local session and navigates to login. Always clears session even if the request fails.
   */
  logout(): void {
    if (!this.token()) {
      this.clearLocalSession();
      void this.router.navigate(['/auth/login']);
      return;
    }
    this.logoutLoading.set(true);
    this.idpAuth
      .logout()
      .pipe(finalize(() => this.logoutLoading.set(false)))
      .subscribe({
        next: () => this.finishLogoutAfterApi(),
        error: () => this.finishLogoutAfterApi(),
      });
  }

  private finishLogoutAfterApi(): void {
    this.clearLocalSession();
    void this.router.navigate(['/auth/login']);
  }

  private clearLocalSession(): void {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_KEY);
      localStorage.removeItem(USER_KEY);
    } catch { /* ignore */ }
    this.token.set(null);
    this.currentUser.set(null);
    this.apiProfile.set(null);
  }

  private storeTokens(access: string, refresh: string): void {
    try {
      localStorage.setItem(TOKEN_KEY, access);
      localStorage.setItem(REFRESH_KEY, refresh);
    } catch { /* ignore storage errors */ }
    this.token.set(access);
  }

  /** Decode user info from JWT payload (no signature verification needed on client). */
  decodeUser(): UserInfo | null {
    const tok = this.token();
    if (!tok) return null;
    try {
      const payload = JSON.parse(atob(tok.split('.')[1]));
      const id = Number(payload.id ?? payload.sub ?? 0) || 0;
      const username =
        payload.unique_id ?? payload.username ?? String(payload.sub ?? '');
      const user: UserInfo = {
        id,
        username,
        email: payload.email ?? '',
        full_name:
          payload.full_name ??
          payload.username ??
          payload.unique_id ??
          username,
        is_active: true,
        role: payload.user_type ?? payload.role ?? 'user',
      };
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      this.currentUser.set(user);
      return user;
    } catch { return null; }
  }


  displayName(): string {
    const p = this.apiProfile();
    if (p) {
      const dn = p.display_name?.trim();
      if (dn) return dn;
      const parts = [p.first_name, p.middle_name, p.last_name].filter(
        (x): x is string => !!x?.trim(),
      );
      if (parts.length) return parts.join(' ');
    }
    const u = this.currentUser();
    return u?.full_name || u?.username || 'User';
  }

  displayInitials(): string {
    const name = this.displayName();
    if (!name || name === 'User') return 'U';
    return name
      .split(/\s+/)
      .map((p) => p[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  fetchUserProfile(): Observable<ProfileResponse | null> {
    if (!this.token()) return of(null);
    return this.profileService.getProfile().pipe(
      tap((res) => {
        if (res?.status && res.data) {
          this.apiProfile.set(res.data);
        }
      }),
      catchError(() => of(null)),
    );
  }
}
